import { getCurrentReceivedUrl } from "@/common/currentPageUrl";
import { getHostnameFromUrl, getPublicSubdomainPart } from "@/common/domain";
import { postRequest } from "@/v2/libs/axios";
import { AgentSchema } from "@/v2/types/client";
import {
  RagResponse,
  PostFormDataRequest,
  PostFormDataResponse,
  AgentRequest,
  StartWebChatRequest,
  PostChatMessageRequest,
  GetRAGInfoRequest,
  ChatResponse,
  StartWebChatResponse,
  CreateSessionChatRequest,
  CreateSessionChatResponse,
} from "@/v2/types/schema";
import { webConfig } from "@/v2/utils/config";
import { AUTH_KEY } from "@/v2/utils/constants";

const { VITE_BACKEND_API_URL, VITE_RAG_URL } = webConfig;

// Validate required environment variables
if (!VITE_RAG_URL || !VITE_BACKEND_API_URL) {
  throw new Error(
    "VITE_RAG_URL or VITE_BACKEND_API_URL is not defined in the environment variables.",
  );
}

export const getAgent = async (request: AgentRequest) => {
  const response = await postRequest<AgentSchema, AgentRequest>(
    "/web-agent-data",
    request,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response;
};

export const createSessionChat = async (request: CreateSessionChatRequest) => {
  const response = await postRequest<
    CreateSessionChatResponse,
    CreateSessionChatRequest
  >(`/web-create-session-chat/${request.agent_id}`, request);

  return response;
};

export const startWebChat = async (
  request: StartWebChatRequest & { agent_id: string },
) => {
  const response = await postRequest<StartWebChatResponse, StartWebChatRequest>(
    `/start-web-chat/${request.agent_id}`,
    request,
  );

  return response;
};

export async function postChatMessage(
  request: PostChatMessageRequest & { agent_id: string },
): Promise<ChatResponse> {
  const { agent_id, ...data } = request;
  const response = await postRequest<ChatResponse, PostChatMessageRequest>(
    `/web-agent-chat/${agent_id}`,
    data,
  );

  return response;
}

export async function postChatStreamingMessage(
  request: PostChatMessageRequest & { agent_id: string; chat_id: string },
) {
  const { agent_id, ...data } = request;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const authData = JSON.parse(localStorage.getItem(AUTH_KEY) || "{}");
  // Authorization
  if (authData) {
    headers.Authorization = `Bearer ${authData.access_token}`;
  }

  const receivedUrl = getCurrentReceivedUrl() || document.referrer || "";
  if (receivedUrl) {
    const hostname = getHostnameFromUrl(receivedUrl);
    const publicPage = hostname ? getPublicSubdomainPart(hostname) : null;
    if (publicPage) {
      headers["public-page"] = publicPage;
    }
  }
  const response = await fetch(
    `${VITE_BACKEND_API_URL}/web-agent-chat-streaming/${agent_id}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    },
  );

  return response;
}

/**
 * Get RAG information from external RAG service
 * Note: Uses absolute URL because RAG service may be on a different domain
 * than the main backend API
 *
 * @param request - RAG info request with agent_id
 * @returns RAG response data
 * @throws Error if VITE_RAG_URL is not defined
 */
export async function getRAGInfo(
  request: GetRAGInfoRequest & { agent_id: string },
): Promise<RagResponse> {
  if (!VITE_RAG_URL) {
    throw new Error(
      "RAG URL is not configured. Please check VITE_RAG_URL environment variable.",
    );
  }

  const { agent_id, ...data } = request;
  const response = await postRequest<RagResponse, GetRAGInfoRequest>(
    `${VITE_RAG_URL}/rag-info/${agent_id}`,
    data,
  );

  return response;
}

export async function postFormData(
  formData: PostFormDataRequest,
): Promise<PostFormDataResponse> {
  const response = await postRequest<PostFormDataResponse, PostFormDataRequest>(
    "/save-forms",
    {
      ...formData,
    },
  );

  return response;
}
